import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ShoppingCart, BarChart2, Monitor, Users, Package, Shield, Menu, Palette, Home } from 'lucide-react';
import UserProfileDropdown from './UserProfileDropdown';
import NotificationsDropdown from './NotificationsDropdown';
import axios from 'axios';

// Secciones (NO tocar sus diseños internos)
import ServicesSection from './sections/ServicesSection';
import ClientsSection from './sections/ClientsSection';
import AccountsSection from './sections/AccountsSection';
import MovementsSection from './sections/MovementsSection';
import UsersSection from './sections/UsersSection';

// Hooks de datos compartidos
import { useServices } from './hooks/useServices';
import { useClients } from './hooks/useClients';
import { useAccounts } from './hooks/useAccounts';
import { useMovements } from './hooks/useMovements';
import { useTotals } from './hooks/useTotals';

/** Temas minimalistas */
const themes = {
  darkOld: {
    name: 'Dark Old',
    bgPrimary: 'bg-gray-900',
    bgSecondary: 'bg-gray-800',
    bgTertiary: 'bg-gray-700',
    borderColor: 'border-gray-900',
    textPrimary: 'text-white',
    textSecondary: 'text-gray-300',
    textMuted: 'text-gray-400',
    accentColor: 'bg-purple-600',
    hoverAccent: 'hover:bg-purple-500'
  },
  dark: {
    name: 'Oscuro',
    bgPrimary: 'bg-neutral-950',
    bgSecondary: 'bg-neutral-900',
    bgTertiary: 'bg-neutral-800',
    borderColor: 'border-neutral-800',
    textPrimary: 'text-neutral-100',
    textSecondary: 'text-neutral-300',
    textMuted: 'text-neutral-400',
    accentColor: 'bg-neutral-700',
    hoverAccent: 'hover:bg-neutral-600'
  },
  slate: {
    name: 'Slate',
    bgPrimary: 'bg-slate-50',
    bgSecondary: 'bg-white',
    bgTertiary: 'bg-slate-100',
    borderColor: 'border-slate-200',
    textPrimary: 'text-slate-900',
    textSecondary: 'text-slate-700',
    textMuted: 'text-slate-500',
    accentColor: 'bg-slate-900',
    hoverAccent: 'hover:bg-slate-800'
  },
  bone: {
    name: 'Bone',
    bgPrimary: 'bg-stone-50',
    bgSecondary: 'bg-white',
    bgTertiary: 'bg-stone-100',
    borderColor: 'border-stone-200',
    textPrimary: 'text-stone-900',
    textSecondary: 'text-stone-700',
    textMuted: 'text-stone-500',
    accentColor: 'bg-stone-900',
    hoverAccent: 'hover:bg-stone-800'
  }
};

const DashboardMinimal = () => {
  const [activeSection, setActiveSection] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showThemeSelector, setShowThemeSelector] = useState(false);
  const [currentTheme, setCurrentTheme] = useState('dark');
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, csrfToken } = useAuth();
  const themeSelectorRef = useRef(null);

  const { services, fetchServices } = useServices();
  const { clients, fetchClients } = useClients();
  const { accounts } = useAccounts();
  const { } = useMovements();
  const { totals } = useTotals();

  const changeTheme = (t) => { setCurrentTheme(t); localStorage.setItem('netfly-theme', t); setShowThemeSelector(false); };
  const toggleSidebar = () => setSidebarCollapsed((s) => !s);

  // Inicialización una sola vez (tema + datos)
  useEffect(() => {
    const saved = localStorage.getItem('netfly-theme');
    if (saved && themes[saved]) setCurrentTheme(saved);
    
    // Cargar datos iniciales con deduplicación automática
    (async () => {
      await Promise.all([
        fetchServices(1, 50), // Cargamos más servicios
        fetchClients(1, 1000) // Cargamos muchos más clientes para los selects
      ]);
    })();
  }, []);

  // Event listeners (click outside)
  useEffect(() => {
    const out = (e) => {
      if (themeSelectorRef.current && !themeSelectorRef.current.contains(e.target)) {
        setShowThemeSelector(false);
      }
    };
    document.addEventListener('mousedown', out);
    return () => document.removeEventListener('mousedown', out);
  }, []);

  // Efectos reactivos (CSRF y location)
  useEffect(() => {
    if (csrfToken) {
      axios.defaults.headers.common['X-CSRF-Token'] = csrfToken;
    }
  }, [csrfToken]);

  useEffect(() => {
    if (location.pathname.includes('/admin/users')) {
      setActiveSection('usuarios');
    }
  }, [location]);

  const handleSectionChange = (section) => {
    if (section === 'usuarios' && currentUser?.role === 'admin') navigate('/admin/users');
    else setActiveSection(section);
  };

  // Home minimalista
  const renderDashboardHome = () => {
    const stats = [
      { title: 'Clientes', value: totals.clients, icon: Users },
      { title: 'Servicios', value: totals.services, icon: Package },
      { title: 'Cuentas', value: totals.accounts, icon: Monitor },
      { title: 'Movimientos', value: totals.movements, icon: BarChart2 }
    ];

    return (
      <div className="p-6 md:p-8 space-y-8">
        <div className={`rounded-xl border ${themes[currentTheme].borderColor} ${themes[currentTheme].bgSecondary} p-5 flex items-center justify-between`}>
          <div>
            <h1 className={`text-xl md:text-2xl font-medium ${themes[currentTheme].textPrimary}`}>Hola, {currentUser?.name || currentUser?.username}</h1>
            <p className={`mt-1 text-sm ${themes[currentTheme].textMuted}`}>Accesos rápidos y resumen</p>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat, i) => (
            <div key={i} className={`rounded-xl border ${themes[currentTheme].borderColor} ${themes[currentTheme].bgSecondary} p-4`}>
              <div className="flex items-center gap-3">
                <stat.icon size={18} className={themes[currentTheme].textMuted} />
                <div className="ml-auto text-right">
                  <p className={`text-xs ${themes[currentTheme].textMuted}`}>{stat.title}</p>
                  <p className={`text-lg font-medium ${themes[currentTheme].textPrimary}`}>{stat.value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div className={`rounded-xl border ${themes[currentTheme].borderColor} ${themes[currentTheme].bgSecondary} p-5`}>
            <h3 className={`text-base font-medium mb-3 ${themes[currentTheme].textPrimary}`}>Acciones rápidas</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[
                { label: 'Nuevo Cliente', section: 'clientes', icon: Users },
                { label: 'Nuevo Servicio', section: 'servicios', icon: Package },
                { label: 'Nueva Cuenta', section: 'cuentas', icon: Monitor },
                { label: 'Ver Movimientos', section: 'movimientos', icon: BarChart2 }
              ].map((a, idx) => (
                <button key={idx} onClick={() => handleSectionChange(a.section)} className={`rounded-lg border ${themes[currentTheme].borderColor} px-3 py-2 text-left ${themes[currentTheme].textPrimary} ${themes[currentTheme].bgTertiary} hover:opacity-90`}>
                  <div className="flex items-center gap-2">
                    <a.icon size={16} className={themes[currentTheme].textMuted} />
                    <span className="text-xs font-medium">{a.label}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className={`rounded-xl border ${themes[currentTheme].borderColor} ${themes[currentTheme].bgSecondary} p-5`}>
            <h3 className={`text-base font-medium mb-3 ${themes[currentTheme].textPrimary}`}>Actividad reciente</h3>
            <ul className="space-y-2">
              {[
                { action: 'Cliente nuevo registrado', time: 'Hace 2 h' },
                { action: 'Servicio actualizado', time: 'Hace 4 h' },
                { action: 'Nueva cuenta creada', time: 'Hace 6 h' }
              ].map((act, idx) => (
                <li key={idx} className="flex items-center justify-between">
                  <span className={`text-sm ${themes[currentTheme].textSecondary}`}>{act.action}</span>
                  <span className={`text-xs ${themes[currentTheme].textMuted}`}>{act.time}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'dashboard':
        return renderDashboardHome();
      case 'usuarios':
        return <UsersSection />;
      case 'clientes':
        return <ClientsSection themes={themes} currentTheme={currentTheme} accounts={accounts} services={services} />;
      case 'movimientos':
        return <MovementsSection themes={themes} currentTheme={currentTheme} services={services} clients={clients} />;
      case 'cuentas':
        return <AccountsSection themes={themes} currentTheme={currentTheme} services={services} clients={clients} />;
      case 'servicios':
        return <ServicesSection themes={themes} currentTheme={currentTheme} accounts={accounts} />;
      case 'tienda':
        return (
          <div className="p-8">
            <div className={`rounded-xl border ${themes[currentTheme].borderColor} ${themes[currentTheme].bgSecondary} p-8`}>
              <h2 className={`text-xl font-medium ${themes[currentTheme].textPrimary}`}>Tienda</h2>
              <p className={`mt-2 ${themes[currentTheme].textMuted}`}>Próximamente…</p>
            </div>
          </div>
        );
      default:
        return renderDashboardHome();
    }
  };

  return (
    <div className={`flex h-screen ${themes[currentTheme].bgPrimary} ${themes[currentTheme].textPrimary}`}>
      {/* Sidebar minimal */}
      <div className={`sticky top-0 h-screen ${themes[currentTheme].bgSecondary} border-r ${themes[currentTheme].borderColor} transition-all duration-200 ${sidebarCollapsed ? 'w-[84px]' : 'w-60'} flex flex-col`}>
        {/* Brand / toggle */}
        <div className={`h-14 px-4 md:px-6 flex items-center justify-between border-b ${themes[currentTheme].borderColor}`}>
          {sidebarCollapsed ? (
            <img 
              src="/images/clover-logo.png" 
              alt="Clover Logo" 
              className="w-6 h-6 rounded object-contain"
            />
          ) : (
            <div className="flex items-center gap-2">
              <img 
                src="/images/clover-logo.png" 
                alt="Clover Logo" 
                className="w-6 h-6 rounded object-contain"
              />
              <div className="text-base font-medium">Clover</div>
            </div>
          )}
          <button onClick={toggleSidebar} className={`p-2 rounded ${themes[currentTheme].bgTertiary} hover:opacity-90`}>
            <Menu size={18} />
          </button>
        </div>

        {/* Navegación */}
        <nav className="flex-1 p-3 overflow-y-auto">
          {[
            { key: 'dashboard', label: 'Dashboard', icon: Home },
            { key: 'tienda', label: 'Tienda', icon: ShoppingCart },
            { key: 'movimientos', label: 'Movimientos', icon: BarChart2 },
            { key: 'cuentas', label: 'Cuentas', icon: Monitor },
            { key: 'clientes', label: 'Clientes', icon: Users },
            { key: 'servicios', label: 'Servicios', icon: Package },
            ...(currentUser?.role === 'admin' ? [{ key: 'usuarios', label: 'Usuarios', icon: Shield }] : [])
          ].map(({ key, label, icon: Icon }) => {
            const active = activeSection === key;
            return (
              <button
                key={key}
                onClick={() => handleSectionChange(key)}
                className={`group w-full flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'justify-start px-3'} py-2.5 rounded-lg mb-1.5 border ${active ? themes[currentTheme].accentColor + ' text-white border-transparent' : themes[currentTheme].borderColor + ' hover:border-transparent'} transition-colors`}
              >
                <Icon size={18} className={active ? 'text-white' : themes[currentTheme].textMuted} />
                {!sidebarCollapsed && (
                  <span className={`ml-3 text-sm ${active ? 'font-medium text-white' : themes[currentTheme].textSecondary}`}>{label}</span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Contenido principal */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header minimal */}
        <header className={`${themes[currentTheme].bgSecondary} border-b ${themes[currentTheme].borderColor} sticky top-0 z-10`}>
          <div className="flex items-center justify-between h-14 px-4 md:px-6">
            <div className={`text-base md:text-lg font-medium ${themes[currentTheme].textPrimary}`}>
              {activeSection === 'dashboard' ? 'Panel de Control' : activeSection.charAt(0).toUpperCase() + activeSection.slice(1)}
            </div>
            <div className="flex items-center gap-2">
              <NotificationsDropdown />

              {/* Selector de tema */}
              <div ref={themeSelectorRef} className="relative">
                <button onClick={() => setShowThemeSelector(!showThemeSelector)} className={`rounded-lg ${themes[currentTheme].bgTertiary} p-2 hover:opacity-90`}>
                  <Palette size={16} />
                </button>
                {showThemeSelector && (
                  <div className={`absolute right-0 mt-2 w-48 ${themes[currentTheme].bgSecondary} rounded-lg border ${themes[currentTheme].borderColor} shadow z-20`}>
                    <div className="p-2">
                      {Object.entries(themes).map(([key, t]) => (
                        <button key={key} onClick={() => changeTheme(key)} className={`w-full flex items-center justify-between px-3 py-2 rounded ${currentTheme === key ? t.accentColor + ' text-white' : ''}`}>
                          <span className={`text-sm ${currentTheme === key ? 'text-white' : themes[currentTheme].textPrimary}`}>{t.name}</span>
                          <span className={`w-3 h-3 rounded-full ${key==='dark'?'bg-neutral-600':key==='slate'?'bg-slate-900':'bg-stone-900'}`} />
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <UserProfileDropdown />
            </div>
          </div>
        </header>

        {/* Main */}
        <main className={`flex-1 overflow-y-auto ${themes[currentTheme].bgPrimary}`}>{renderActiveSection()}</main>
      </div>
    </div>
  );
};

export default DashboardMinimal;
